import { useId, useRef } from "react";
import clsx from "clsx";

import { useUploads } from "../lib/uploadQueue";
import { FILE_ACCEPT } from "../lib/upload";

export type CaptureButtonProps = {
  /**
   * `thumb` is the big round action in the bottom bar — the one you can reach
   * one-handed. `bar` sits in the masthead on wide screens. `inline` is for
   * empty states.
   */
  variant?: "thumb" | "bar" | "inline";
  label?: string;
  className?: string;
};

/**
 * The one thing this app is for.
 *
 * Deliberately no `capture` attribute: on iOS that would jump straight to the
 * camera and drop the "Photo Library" option, and it can only ever return one
 * photograph. Without it you get the native sheet — camera *or* a multi-select
 * from the library, which is what people actually want.
 *
 * The uploads themselves are handed to the queue at the root of the app, so they
 * keep running wherever the user goes next.
 */
export function CaptureButton({ variant = "thumb", label, className }: CaptureButtonProps) {
  const id = useId();
  const input = useRef<HTMLInputElement | null>(null);
  const { enqueue } = useUploads();

  const text = label ?? (variant === "inline" ? "Photograph something" : "Add a find");

  const open = () => input.current?.click();

  return (
    <>
      <input
        ref={input}
        id={id}
        type="file"
        accept={FILE_ACCEPT}
        multiple
        className="sr-only"
        onChange={(event) => {
          const { files } = event.target;
          if (files && files.length > 0) enqueue(files);
          // Let the same file be chosen twice in a row.
          event.target.value = "";
        }}
      />

      {variant === "thumb" ? (
        <button
          type="button"
          onClick={open}
          aria-label={text}
          className={clsx(
            "group relative flex size-16 shrink-0 items-center justify-center rounded-full border-2 border-ink bg-ochre text-ink shadow-lifted transition-transform duration-200 active:scale-95",
            className,
          )}
        >
          <ApertureGlyph className="size-8" />
          <span className="sr-only">{text}</span>
        </button>
      ) : variant === "bar" ? (
        <button
          type="button"
          onClick={open}
          className={clsx(
            "touch inline-flex items-center gap-2 border border-ink bg-ochre px-4 py-2 font-label text-sm font-semibold tracking-wide text-ink transition-transform duration-200 active:scale-[0.98]",
            className,
          )}
        >
          <ApertureGlyph className="size-4" />
          {text}
        </button>
      ) : (
        <button
          type="button"
          onClick={open}
          className={clsx(
            "touch inline-flex items-center gap-2.5 border-2 border-ink bg-ochre px-5 py-3 font-label text-base font-semibold tracking-wide text-ink shadow-sheet transition-transform duration-200 active:scale-[0.98]",
            className,
          )}
        >
          <ApertureGlyph className="size-5" />
          {text}
        </button>
      )}
    </>
  );
}

/** A camera aperture, drawn as a botanical rosette. */
function ApertureGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="none">
      <circle cx="12" cy="12" r="9.2" stroke="currentColor" strokeWidth="1.6" />
      {[0, 60, 120, 180, 240, 300].map((angle) => (
        <path
          key={angle}
          d="M12 12 L12 2.8 A9.2 9.2 0 0 1 19.97 7.4 Z"
          transform={`rotate(${angle} 12 12)`}
          stroke="currentColor"
          strokeWidth="1.1"
          strokeLinejoin="round"
          opacity="0.55"
        />
      ))}
      <circle cx="12" cy="12" r="2.4" fill="currentColor" />
    </svg>
  );
}
